import { apiGet, apiSend } from './client'

/**
 * What the company pays for: the tools, the seats and the rupees.
 *
 * Money is rupees throughout, carried as whole paise and only turned into a
 * decimal for display. A float would round, and a total that is a rupee out
 * from the card statement is a total nobody trusts twice. There is no
 * currency to choose: whatever a vendor bills in, the card is charged in
 * rupees with their rate and fees already in it.
 */

export type SubscriptionPerson = {
  id: string
  display_name: string
  initials: string
  email: string
  has_avatar: boolean
  /** False for somebody suspended or never activated, who should not still hold a seat. */
  active: boolean
}

export type SubscriptionCharge = {
  /** The billing period this payment settles, where anybody said. */
  covers_from?: string | null
  covers_to?: string | null
  id: string
  subscription_id: string
  /** What the card was charged, in paise. */
  amount_paise: number
  /** "YYYY-MM-DD". */
  charged_on: string
  /** How it was paid: card, net_banking, upi, bank_transfer, other. */
  method: PaymentMethod
  method_label: string
  note: string
  recorded_at: string
}

/** A one-off is a plugin or a licence bought outright: no renewal, no run rate. */
export type BillingCycle = 'monthly' | 'yearly' | 'one_time'
export type SubscriptionStatus = 'active' | 'paused' | 'cancelled'
export type PaymentMethod = 'card' | 'net_banking' | 'upi' | 'bank_transfer' | 'other'
export type PricingModel = 'per_seat' | 'flat'

export type Subscription = {
  id: string
  name: string
  plan: string
  category: string
  category_label: string
  /** A key into the client's own logo set, or null for initials. */
  logo: string | null
  website: string
  /** The listed price in paise: what the vendor asks. Shown, never summed. */
  price_minor: number
  pricing_model: PricingModel
  billing_cycle: BillingCycle
  seats: number | null
  holders: SubscriptionPerson[]
  /** Seats held by somebody with no Medulla account: shared mailboxes and the like. */
  external_holders: string[]
  /** The due date: when the vendor next bills. */
  next_renewal_on: string | null
  /** What the vendor says is owed, in paise, and when that was checked. */
  outstanding_paise: number
  outstanding_as_of: string | null
  /** Amount due at the next renewal: the outstanding balance, or the plan's monthly cost. */
  amount_due_paise: number | null
  /** The due date has passed with no payment recorded since. */
  overdue: boolean
  owner: SubscriptionPerson | null
  /** When the agreement began, as against when it was entered here. */
  started_on?: string | null
  /** Whether this viewer may change this one: they manage them all, or they own it. */
  can_manage?: boolean
  notes: string
  status: SubscriptionStatus
  status_label: string
  /** The day it was paused or cancelled, null while active. */
  status_since: string | null
  last_charge: SubscriptionCharge | null
  /** The expected monthly bill from the plan, before tax. Null when no price is set. */
  monthly_paise: number | null
  /**
   * Whether it renews itself. Null where nobody knows, which is most things;
   * set by a registrar sync for a domain. Optional because the query cache
   * outlives a release, and a row saved before the field existed must still
   * render.
   */
  auto_renews?: boolean | null
  /** The registrar keeping it in step, "GoDaddy", or null for anything typed in. */
  registrar?: string | null
  created_at: string
  updated_at: string
}

/** Something waiting on a person: money to pay, or a payment to write down. */
export type SubscriptionAction = {
  kind: 'payment_overdue' | 'payment_due' | 'payment_missing' | 'never_recorded'
  /** The amount is worked out from the plan, not a balance the vendor stated. */
  estimated?: boolean
  kind_label: string
  subscription_id: string
  subscription_name: string
  logo: string | null
  on: string | null
  amount_paise: number | null
  period: string
  /** Money already late, as against money still ahead. */
  late: boolean
  detail: string
}

export type SpendSummary = {
  this_month_paise: number
  last_month_paise: number
  monthly_run_rate_paise: number
  /** Active subscriptions with no price entered, so absent from the run rate. */
  unpriced_count: number
  active_count: number
  seats_paid: number
  seats_held: number
  outstanding_paise: number
  due_soon_paise: number
  overdue_count: number
  overdue_paise: number
  months: Array<{ month: string; amount_paise: number }>
  by_category: Array<{ value: string; label: string; amount_paise: number }>
  renewing_soon: Array<{
    id: string
    name: string
    next_renewal_on: string
    amount_due_paise: number | null
    overdue: boolean
  }>
  actions: SubscriptionAction[]
  action_paise: number
}

/**
 * Defensive, like the seat helpers beside it: the query cache is persisted
 * across releases, so a payload written before this field existed is read
 * back by the new page on the first frame after a deploy.
 */
export function actionsOf(summary: SpendSummary | undefined): SubscriptionAction[] {
  return summary?.actions ?? []
}

export type SubscriptionInput = {
  name: string
  plan?: string
  category?: string
  logo?: string | null
  website?: string
  price_minor?: number
  pricing_model?: PricingModel
  billing_cycle?: BillingCycle
  seats?: number | null
  holder_ids?: string[]
  external_holders?: string[]
  next_renewal_on?: string | null
  started_on?: string | null
  owner_id?: string | null
  notes?: string
  outstanding_paise?: number
  outstanding_as_of?: string | null
}

export type IntegrationProvider = 'google_workspace' | 'digitalocean' | 'figma' | 'anthropic' | 'openai'

/** What a screen may know about an integration: never the credential. */
export type Integration = {
  provider: IntegrationProvider
  provider_label: string
  configured: boolean
  syncs_seats: boolean
  reports_invoices: boolean
  settings: Record<string, string>
  last_synced_at: string | null
  last_result: string
  last_run_failed: boolean
}

export function fetchIntegration(subscriptionId: string, signal?: AbortSignal): Promise<Integration | null> {
  return apiGet<Integration | null>(`/subscription/${subscriptionId}/integration`, { signal })
}

/** An empty credential keeps whatever is stored, which is what editing a domain means. */
export function configureIntegration(
  subscriptionId: string,
  body: { provider: IntegrationProvider; credential: string; settings: Record<string, string> },
): Promise<null> {
  return apiSend<null>(`/subscription/${subscriptionId}/integration`, 'PUT', body)
}

export function removeIntegration(subscriptionId: string): Promise<null> {
  return apiSend<null>(`/subscription/${subscriptionId}/integration`, 'DELETE')
}

export function syncIntegration(subscriptionId: string): Promise<{ result: string } | null> {
  return apiSend<{ result: string }>(`/subscription/${subscriptionId}/integration/sync`, 'POST')
}

export function testIntegration(subscriptionId: string): Promise<{ result: string } | null> {
  return apiSend<{ result: string }>(`/subscription/${subscriptionId}/integration/test`, 'POST')
}

/**
 * A domain registrar the company's domains are read from.
 *
 * `connected` and nothing else about the token: a screen that can show a
 * token is a screen that can leak one.
 */
export type RegistrarConnection = {
  registrar: 'godaddy'
  label: string
  connected: boolean
  last_synced_at: string | null
  last_result: string
  last_run_failed: boolean
}

export function fetchRegistrar(signal?: AbortSignal): Promise<RegistrarConnection> {
  return apiGet<RegistrarConnection>('/subscription/registrar/godaddy', { signal })
}

/** Proved against GoDaddy before it is kept. An empty credential keeps the stored one. */
export function connectRegistrar(credential: string): Promise<{ result: string } | null> {
  return apiSend<{ result: string }>('/subscription/registrar/godaddy', 'PUT', { credential })
}

export function syncRegistrar(): Promise<{ result: string } | null> {
  return apiSend<{ result: string }>('/subscription/registrar/godaddy/sync', 'POST')
}

/** Forgets the token. Every domain it imported is kept. */
export function disconnectRegistrar(): Promise<null> {
  return apiSend<null>('/subscription/registrar/godaddy', 'DELETE')
}

export function fetchSubscriptions(signal?: AbortSignal): Promise<Subscription[]> {
  return apiGet<Subscription[]>('/subscription', { signal })
}

export function fetchSpendSummary(signal?: AbortSignal): Promise<SpendSummary> {
  return apiGet<SpendSummary>('/subscription/summary', { signal })
}

export function fetchSubscriptionCharges(id: string, signal?: AbortSignal): Promise<SubscriptionCharge[]> {
  return apiGet<SubscriptionCharge[]>(`/subscription/${id}/charge`, { signal })
}

/** The server answers with nobody until two characters are sent. */
export function fetchSubscriptionPeople(search: string, signal?: AbortSignal): Promise<SubscriptionPerson[]> {
  return apiGet<SubscriptionPerson[]>('/subscription/person', { query: { search }, signal })
}

export function addSubscription(body: SubscriptionInput): Promise<{ id: string } | null> {
  return apiSend<{ id: string }>('/subscription', 'POST', body)
}

export function updateSubscription(id: string, body: SubscriptionInput): Promise<null> {
  return apiSend<null>(`/subscription/${id}`, 'PATCH', body)
}

export function setSubscriptionSeats(
  id: string,
  seats: number | null,
  holderIds: string[],
  externalHolders: string[] = [],
): Promise<null> {
  return apiSend<null>(`/subscription/${id}/seat`, 'PUT', {
    seats,
    holder_ids: holderIds,
    external_holders: externalHolders,
  })
}

export function setSubscriptionStatus(
  id: string,
  status: SubscriptionStatus,
  statusSince: string | null = null,
): Promise<null> {
  return apiSend<null>(`/subscription/${id}/status`, 'PUT', { status, status_since: statusSince })
}

export function deleteSubscription(id: string): Promise<null> {
  return apiSend<null>(`/subscription/${id}`, 'DELETE')
}

export function recordCharge(
  id: string,
  body: {
    amount_paise: number
    charged_on: string
    advance_renewal?: boolean
    method?: PaymentMethod
    note?: string
    /** The billing period this settles, where somebody says: "2026-03-01". */
    covers_from?: string | null
    covers_to?: string | null
  },
): Promise<{ id: string } | null> {
  return apiSend<{ id: string }>(`/subscription/${id}/charge`, 'POST', body)
}

export type SpendReport = {
  from: string
  to: string
  group_by: 'month' | 'year'
  total_paise: number
  payment_count: number
  periods: Array<{ period: string; label: string; amount_paise: number }>
  by_subscription: Array<{ id: string; name: string; logo: string | null; amount_paise: number; payment_count: number }>
  by_category: Array<{ value: string; label: string; amount_paise: number }>
  by_method: Array<{ value: string; label: string; amount_paise: number; payment_count: number }>
  payments: Array<{
    id: string
    subscription_id: string
    subscription_name: string
    logo: string | null
    charged_on: string
    amount_paise: number
    method: PaymentMethod
    method_label: string
    note: string
  }>
  /** The same length of time immediately before this window. */
  previous_total_paise?: number
  movers?: Array<{
    id: string
    name: string
    logo: string | null
    amount_paise: number
    previous_paise: number
    change_paise: number
  }>
  /** What the active plans commit the company to over the next twelve months. */
  annual_commitment_paise?: number
  /** Paid-for seats nobody holds, as a monthly figure. */
  spare_seat_paise?: number
  seats_paid?: number
  seats_held?: number
  seat_waste?: Array<{
    id: string
    name: string
    logo: string | null
    monthly_paise: number | null
    seats: number
    held: number
    spare_paise: number
  }>
  renewal_months?: Array<{ month: string; label: string; amount_paise: number; count: number }>
  /** Active subscriptions with no payment recorded inside this window. */
  unrecorded_count?: number
}

/** One month of a subscription's life. */
export type TimelineMonth = {
  month: string
  label: string
  status: '' | SubscriptionStatus
  status_label: string
  amount_paise: number
  payment_count: number
  /** Running that month with nothing recorded against it. */
  unpaid: boolean
  events: string[]
}

/** Month by month: what it was doing, and what was paid. Newest first. */
export function fetchSubscriptionTimeline(id: string, signal?: AbortSignal): Promise<TimelineMonth[]> {
  return apiGet<TimelineMonth[]>(`/subscription/${id}/timeline`, { signal })
}

/** Everything paid in a window, by period, subscription, category and method. */
export function fetchSpendReport(
  query: { from?: string; to?: string; group_by?: 'month' | 'year'; subscription_id?: string },
  signal?: AbortSignal,
): Promise<SpendReport> {
  return apiGet<SpendReport>('/subscription/report', { query, signal })
}

export function deleteCharge(chargeId: string): Promise<null> {
  return apiSend<null>(`/subscription/charge/${chargeId}`, 'DELETE')
}

/**
 * Rupees, the way an Indian statement writes them: ₹1,74,250 rather than
 * ₹174,250. `en-IN` groups in lakhs and crores, which is what the number is
 * read as out loud.
 *
 * Whole rupees by default, because a page of totals to the paise is a page
 * of noise; `exact` keeps them where the figure is a single charge somebody
 * is checking against a statement.
 */
export function formatRupees(paise: number, exact = false): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: exact ? 2 : 0,
    maximumFractionDigits: exact ? 2 : 0,
  }).format(paise / 100)
}

/**
 * Seat holders, tolerating a payload from an older release.
 *
 * The query cache is kept in the browser across releases, so a screen can
 * open on a subscription saved before a field existed - and reading
 * `.length` off the missing one crashes the page rather than degrading.
 * Every screen reads seats through these two.
 */
export function holdersOf(subscription: Subscription): SubscriptionPerson[] {
  return subscription.holders ?? []
}

export function externalHoldersOf(subscription: Subscription): string[] {
  return subscription.external_holders ?? []
}

/** Seats taken, whoever holds them. */
export function assignedSeats(subscription: Subscription): number {
  return holdersOf(subscription).length + externalHoldersOf(subscription).length
}

/** The listed price: what the vendor asks, as against what was charged. */
export function formatListedPrice(subscription: Subscription): string {
  const per = subscription.pricing_model === 'per_seat' ? '/seat' : ''
  const price = `${formatRupees(subscription.price_minor)}${per}`

  if (subscription.billing_cycle === 'one_time') return `${price} once`

  return `${price}/${subscription.billing_cycle === 'yearly' ? 'yr' : 'mo'}`
}

/** Paise from what somebody typed in rupees: "1,742.50" becomes 174250. */
export function rupeesToPaise(value: string): number | null {
  const cleaned = value.replace(/[^0-9.]/g, '')

  if (cleaned === '' || !/^\d*\.?\d{0,2}$/.test(cleaned)) return null

  // Rounded rather than truncated: 17.425 typed by hand is 17.43, and
  // parseFloat's own binary error must not lose a paisa either way.
  return Math.round(Number.parseFloat(cleaned) * 100)
}
